/**
 * IdentityOrganizationPageV2 — Phase 6D (P0-IDENT).
 *
 * Organization-scope detail page. The "base of the inheritance chain":
 * every value here becomes the default that tenants and agents resolve
 * to unless they install an override.
 *
 * Section composition matches the approved mockup (2026-05):
 *   1. Fleet KPI strip — same shape as the landing page, scoped to this org
 *   2. Trust Domains table — the SPIFFE identity boundaries owned here
 *   3. Federation + Root CA two-column split
 *   4. SPIRE bootstrap (server topology + attestation methods)
 *   5. Rotation Defaults + Compliance Bindings two-column split
 *   6. Recent Changes footer (auditor reflex check)
 *
 * Compliance Bindings row maps directly to industry_risk_default.rego —
 * each row activates one of HIPAA-MINOR, SOX-AUTONOMOUS-FIN,
 * FISMA-COGNITIVE-GOV, NERC-CIP-CRITICAL-ENERGY, PROBATION-CRITICAL-WASM.
 *
 * Data: stub data shaped after GET /api/v1/identity/organization, which
 * will be implemented as IdentityScopeController in 6F.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, Share2, Award, ServerCog, RefreshCw, Scale, History,
  AlertTriangle, CheckCircle2, ArrowRight, Map, Settings, Key,
  Download, Activity, Plus, Save, ArrowUpRight, Edit3,
} from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { OrgConfigEditSlideout } from '../components/v2/identity/OrgConfigEditSlideout';
import { getIdentityOrganization } from '../services/api';

/* ────────────────────────────────────────────────────────────────
   Page data — stub. Backed by GET /api/v1/identity/organization in 6F.
   ──────────────────────────────────────────────────────────────── */

const KPIS = [
  { label: 'Trust domains',     value: '3',     sub: '2 federated, 1 internal',  tone: 'success' },
  { label: 'SPIRE servers',     value: '4 / 4', sub: 'All healthy · HA quorum',  tone: 'success' },
  { label: 'Root CA expiry',    value: '847 d', sub: '2028-09-12 18:00 UTC',     tone: 'muted' },
  { label: 'Last config change',value: '3d ago',sub: 'k.morimoto@acmebank',      tone: 'muted' },
];

const TRUST_DOMAINS = [
  {
    id: 'primary',
    spiffeId: 'spiffe://acmebank.spiffe.io',
    note: 'Primary · CA: AcmeBank-Root-2026',
    role: 'Primary',
    roleTone: 'trust',
    bundle: { label: 'Self · live', tone: 'success' },
    svidsMinted: 247,
  },
  {
    id: 'partners',
    spiffeId: 'spiffe://partners.acmebank.spiffe.io',
    note: 'Partner-facing · CA: External-Partners-2026',
    role: 'Federated',
    roleTone: 'success',
    bundle: { label: '2h ago · live', tone: 'success' },
    svidsMinted: 38,
  },
  {
    id: 'sandbox',
    spiffeId: 'spiffe://sandbox.acmebank.spiffe.io',
    note: 'Non-prod sandbox · CA: Sandbox-2025',
    role: 'Internal',
    roleTone: 'warning',
    bundle: { label: '26h ago · stale', tone: 'warning' },
    svidsMinted: 14,
  },
];

const FEDERATIONS = [
  {
    id: 'finnet',
    name: 'FinNet Clearing',
    spiffeId: 'spiffe://finnet.clearing.io',
    direction: 'Bidirectional',
    directionIcon: CheckCircle2,
    directionTone: 'success',
    bundleAge: 'Bundle 14m ago',
    initials: 'FN',
  },
  {
    id: 'regulator',
    name: 'Regulator Gateway',
    spiffeId: 'spiffe://occ-supervisor.gov',
    direction: 'Inbound only',
    directionIcon: ArrowRight,
    directionTone: 'success',
    bundleAge: 'Bundle 2h ago',
    initials: 'RG',
  },
  {
    id: 'corerisk',
    name: 'CoreRisk Analytics',
    spiffeId: 'spiffe://corerisk.io',
    direction: 'Bundle expired',
    directionIcon: AlertTriangle,
    directionTone: 'danger',
    bundleAge: 'Refresh required',
    initials: 'CR',
    danger: true,
  },
];

const ROOT_CAS = [
  {
    id: 'r2026',
    name: 'AcmeBank-Root-2026',
    status: 'Active',
    statusTone: 'success',
    fingerprint: 'a4:e1:08:bf:2c:…:9d:71',
    facts: [
      { label: 'Algo',    value: 'ECDSA P-384' },
      { label: 'Issued',  value: '2026-01-15' },
      { label: 'Expires', value: '2028-09-12' },
    ],
  },
  {
    id: 'r2023',
    name: 'AcmeBank-Root-2023',
    status: 'Retired',
    statusTone: 'muted',
    fingerprint: '7b:c2:14:9f:0e:…:33:af',
    footnote: 'Kept in bundle for verification of in-flight SVIDs · drops 2026-08-01',
  },
];

const SPIRE_BOOTSTRAP = {
  servers: [
    'spire-east.acmebank.io:8081',
    'spire-west.acmebank.io:8081',
  ],
  topology: '4 nodes · Postgres datastore · HA',
  nodeAttestation: ['aws_iid', 'k8s_psat', 'tpm'],
  nodeAttestationNote: '3 methods enabled · aws_iid is default',
  workloadAttestation: ['k8s', 'unix', 'docker'],
  workloadAttestationNote: 'Selectors validated against allowlist',
};

const ROTATION_DEFAULTS = [
  { label: 'Default SVID TTL', value: '1 hour' },
  { label: 'Rotation window',  value: '⅔ of TTL' },
  { label: 'JWT-SVID TTL',     value: '5 minutes' },
  { label: 'CA TTL',           value: '24 hours' },
  { label: 'Failure threshold',value: '3 strikes / 15m' },
  { label: 'Escalation',       value: 'PagerDuty P2' },
];

/* Compliance bindings — each row maps to one rule in
 * industry_risk_default.rego. The `regoTag` field is the exact tag the
 * policy emits; the audit pipeline matches on it for downstream evidence
 * surfacing. Adding or removing a binding here is the customer's
 * org-wide opt-in for that framework. */
const COMPLIANCE_BINDINGS = [
  {
    id: 'sox',
    framework: 'SOX',
    extra: '+ GLBA',
    regoTag: 'SOX-AUTONOMOUS-FIN',
    status: 'Enforced',
    statusTone: 'action',
    dotTone: 'action',
  },
  {
    id: 'hipaa',
    framework: 'HIPAA',
    regoTag: 'HIPAA-MINOR',
    status: 'Tag only',
    statusTone: 'success',
    dotTone: 'success',
  },
  {
    id: 'fisma',
    framework: 'FISMA',
    extra: '+ NIST 800-53',
    regoTag: 'FISMA-COGNITIVE-GOV',
    status: 'Off',
    statusTone: 'muted',
    dotTone: 'muted',
    disabled: true,
  },
  {
    id: 'nerc',
    framework: 'NERC-CIP',
    regoTag: 'NERC-CIP-CRITICAL-ENERGY',
    status: 'Off',
    statusTone: 'muted',
    dotTone: 'muted',
    disabled: true,
  },
  {
    id: 'probation',
    framework: 'Probation watch',
    extra: 'cross-cutting',
    regoTag: 'PROBATION-CRITICAL-WASM',
    status: 'Enforced',
    statusTone: 'trust',
    dotTone: 'trust',
  },
];

const RECENT_CHANGES = [
  { action: 'Federated', target: 'spiffe://corerisk.io', note: '— bidirectional', who: 'k.morimoto', when: '3d ago' },
  { action: 'Enabled',   target: 'SOX + GLBA binding',   note: '',                who: 'k.morimoto', when: '8d ago' },
  { action: 'Rotated',   target: 'AcmeBank-Root-2023 → AcmeBank-Root-2026', note: '', who: 'automation', when: '18d ago' },
];

/* Static class maps for compliance dot colors — see Tailwind JIT note
 * in IdentityLandingPageV2 (dynamic concatenation would be dead-stripped).
 * The `muted` variant uses `bg-v2-border` (the DEFAULT key of the
 * v2-border palette in tailwind.config.js) — not `bg-v2-border-default`,
 * which is NOT a real Tailwind class (the DEFAULT key gets exposed as
 * the bare token name, not as `-default`). */
const DOT_BG = {
  action:  'bg-v2-action',
  success: 'bg-v2-success',
  trust:   'bg-v2-trust',
  muted:   'bg-v2-border',
};

/* ────────────────────────────────────────────────────────────────
   IdentityOrganizationPageV2 — top-level export
   ──────────────────────────────────────────────────────────────── */

export default function IdentityOrganizationPageV2() {
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  /* Stage 5-4 — page now fetches the backend to get the real Org
   * metadata (name, domain, real tenant/agent counts). Other sections
   * (KPI strip, trust domains, federations, etc.) still draw from the
   * existing module-level stub data — that's the next Stage flip
   * once each section has a real backing table. For now, the Org
   * IDENTITY (title + subtitle) is data-driven; the section bodies
   * stay stable for no-regression. */
  const [orgData, setOrgData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getIdentityOrganization().then((res) => {
      if (cancelled) return;
      if (res?.ok === false) {
        // eslint-disable-next-line no-console
        console.warn('Identity org fetch failed:', res.error);
      } else {
        setOrgData(res?.data || null);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // V208 — Platform Admin in no-customer-selected mode. Backend returns
  // isPlatformScope=true with all org-specific fields nulled. Render a
  // Customer picker prompt instead of the regular org config layout.
  const isPlatformScope = !!orgData?.isPlatformScope;

  // Title + subtitle derive from real Org metadata when available;
  // graceful fallback to a generic placeholder during the brief
  // fetch window so the page chrome still renders.
  const orgName     = isPlatformScope ? 'Platform' : (orgData?.orgName || 'Organization');
  const tenantCount = orgData?.tenantCount;
  const agentCount  = orgData?.agentCount;
  const subtitleStr = isPlatformScope
    ? (orgData?.pickerHint || 'Select a customer to enter their identity scope.')
    : ((tenantCount != null && agentCount != null)
        ? `Organization-wide identity configuration · inherited by ${tenantCount} tenant${tenantCount === 1 ? '' : 's'} · ${agentCount} agent${agentCount === 1 ? '' : 's'}`
        : 'Organization-wide identity configuration');

  return (
    <V2PageShell
      title={orgName}
      subtitle={subtitleStr}
      backTo="/identity"
      backLabel="Back to Identity"
      tags={
        <>
          <Pill tone="action" size="sm">Organization</Pill>
          <Pill tone="trust"  size="sm" dot>SPIFFE plane</Pill>
        </>
      }
      actions={
        <>
          <Button variant="secondary" size="sm" iconLeft={<History size={14} />}>Audit log</Button>
          {/* Stage 2c-2 — opens OrgConfigEditSlideout. The inert
              "Save changes" button it replaced never had a handler. */}
          <Button
            variant="primary"
            size="sm"
            iconLeft={<Edit3 size={14} />}
            onClick={() => setEditOpen(true)}
          >
            Edit config
          </Button>
        </>
      }
    >
      {/* 1 — Fleet KPI strip ───────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {KPIS.map((k) => (
          <Card key={k.label} padding="sm">
            <div className="text-[11px] uppercase tracking-wide text-v2-ink-muted font-semibold">
              {k.label}
            </div>
            <div className="text-[20px] font-semibold text-v2-ink mt-1 leading-tight">{k.value}</div>
            <div className={
              'text-[11px] mt-0.5 ' + (k.tone === 'success' ? 'text-v2-success' : 'text-v2-ink-secondary')
            }>
              {k.sub}
            </div>
          </Card>
        ))}
      </div>

      {/* 2 — Trust Domains ─────────────────────────────────────── */}
      <Card className="mb-4">
        <Card.Header
          title={<><Globe size={16} className="inline -mt-0.5 mr-1.5 text-v2-action" />Trust domains</>}
          subtitle="SPIFFE identity boundaries owned by this organization"
          actions={<Button variant="secondary" size="xs" iconLeft={<Plus size={12} />}>Add domain</Button>}
        />
        <Card.Body>
          <div className="border border-v2-border-subtle rounded-v2-md overflow-hidden">
            <div className="grid grid-cols-[1.6fr_0.8fr_0.7fr_0.7fr_0.5fr] gap-3 px-4 py-2 bg-v2-muted text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold">
              <div>SPIFFE ID</div>
              <div>Role</div>
              <div>Bundle freshness</div>
              <div>SVIDs minted</div>
              <div></div>
            </div>
            {TRUST_DOMAINS.map((d) => (
              <div
                key={d.id}
                className="grid grid-cols-[1.6fr_0.8fr_0.7fr_0.7fr_0.5fr] gap-3 px-4 py-2.5 border-t border-v2-border-subtle items-center"
              >
                <div>
                  <div className="font-mono-v2 text-[12px] text-v2-ink">{d.spiffeId}</div>
                  <div className="text-[11px] text-v2-ink-muted">{d.note}</div>
                </div>
                <div><Pill tone={d.roleTone} size="sm">{d.role}</Pill></div>
                <div className={'text-[12px] ' + (d.bundle.tone === 'warning' ? 'text-v2-warning' : 'text-v2-success')}>
                  {d.bundle.label}
                </div>
                <div className="text-[12px] text-v2-ink">{d.svidsMinted}</div>
                <div className="text-right"><Button variant="ghost" size="xs">Configure</Button></div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* 3 — Federation + Root CA (two-column split) ───────────── */}
      <div className="grid grid-cols-[1.05fr_1fr] gap-3 mb-4">
        <Card>
          <Card.Header
            title={<><Share2 size={16} className="inline -mt-0.5 mr-1.5 text-v2-success" />Federation</>}
            subtitle="External trust domains we exchange SVIDs with"
            actions={<Button variant="secondary" size="xs" iconLeft={<Plus size={12} />}>Federate</Button>}
          />
          <Card.Body>
            <div className="flex flex-col gap-2">
              {FEDERATIONS.map((f) => {
                const Icon = f.directionIcon;
                return (
                  <div
                    key={f.id}
                    className={
                      'flex items-center gap-3 px-3 py-2 border rounded-v2-md ' +
                      (f.danger
                        ? 'bg-v2-danger-soft border-v2-danger/30'
                        : 'border-v2-border-subtle')
                    }
                  >
                    <div className={
                      'w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ' +
                      (f.danger ? 'bg-v2-danger/20 text-v2-danger' : 'bg-v2-success-soft text-v2-success')
                    }>
                      {f.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={'text-[13px] font-semibold ' + (f.danger ? 'text-v2-danger' : 'text-v2-ink')}>
                        {f.name}
                      </div>
                      <div className={'font-mono-v2 text-[11px] truncate ' + (f.danger ? 'text-v2-danger' : 'text-v2-ink-muted')}>
                        {f.spiffeId}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={
                        'text-[11px] flex items-center gap-1 justify-end ' +
                        (f.directionTone === 'danger' ? 'text-v2-danger' : 'text-v2-success')
                      }>
                        <Icon size={12} aria-hidden="true" />
                        <span>{f.direction}</span>
                      </div>
                      <div className={'text-[10px] ' + (f.danger ? 'text-v2-danger' : 'text-v2-ink-muted')}>
                        {f.bundleAge}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              variant="secondary"
              size="xs"
              iconLeft={<Map size={12} />}
              fullWidth
              className="mt-3"
              onClick={() => navigate('/identity/federation-map')}
              disabled
            >
              Open federation map · Phase 6G
            </Button>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header
            title={<><Award size={16} className="inline -mt-0.5 mr-1.5 text-v2-warning" />Root CA &amp; trust bundle</>}
            subtitle="Roots that every SVID in this org chains back to"
          />
          <Card.Body>
            <div className="flex flex-col gap-2">
              {ROOT_CAS.map((ca) => (
                <div key={ca.id} className="border border-v2-border-subtle rounded-v2-md px-3 py-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[13px] font-semibold text-v2-ink">{ca.name}</div>
                    <Pill tone={ca.statusTone} size="sm">{ca.status}</Pill>
                  </div>
                  <div className="font-mono-v2 text-[11px] text-v2-ink-muted mb-1">
                    SHA-256: {ca.fingerprint}
                  </div>
                  {ca.facts && (
                    <div className="flex gap-4 text-[11px] text-v2-ink-secondary">
                      {ca.facts.map((f) => (
                        <div key={f.label}>
                          {f.label} <span className="text-v2-ink">{f.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {ca.footnote && (
                    <div className="text-[11px] text-v2-ink-secondary">{ca.footnote}</div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="secondary" size="xs" iconLeft={<Key size={12} />} fullWidth>Rotate root</Button>
              <Button variant="secondary" size="xs" iconLeft={<Download size={12} />} fullWidth>Export bundle</Button>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* 4 — SPIRE Bootstrap ───────────────────────────────────── */}
      <Card className="mb-4">
        <Card.Header
          title={<><ServerCog size={16} className="inline -mt-0.5 mr-1.5 text-v2-trust" />SPIRE bootstrap</>}
          subtitle="Server topology and attestation methods for node and workload identity"
        />
        <Card.Body>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">
                Server cluster
              </div>
              {SPIRE_BOOTSTRAP.servers.map((s) => (
                <div key={s} className="font-mono-v2 text-[12px] text-v2-ink">{s}</div>
              ))}
              <div className="text-[11px] text-v2-ink-muted mt-1">{SPIRE_BOOTSTRAP.topology}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">
                Node attestation
              </div>
              <div className="flex flex-wrap gap-1">
                {SPIRE_BOOTSTRAP.nodeAttestation.map((m) => (
                  <Pill key={m} tone="trust" size="sm">{m}</Pill>
                ))}
              </div>
              <div className="text-[11px] text-v2-ink-secondary mt-1.5">{SPIRE_BOOTSTRAP.nodeAttestationNote}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">
                Workload attestation
              </div>
              <div className="flex flex-wrap gap-1">
                {SPIRE_BOOTSTRAP.workloadAttestation.map((m) => (
                  <Pill key={m} tone="success" size="sm">{m}</Pill>
                ))}
              </div>
              <div className="text-[11px] text-v2-ink-secondary mt-1.5">{SPIRE_BOOTSTRAP.workloadAttestationNote}</div>
            </div>
          </div>
        </Card.Body>
        <Card.Footer>
          <Button variant="secondary" size="xs" iconLeft={<Settings size={12} />}>Edit topology</Button>
          <Button variant="secondary" size="xs" iconLeft={<Activity size={12} />}>Health detail</Button>
        </Card.Footer>
      </Card>

      {/* 5 — Rotation Defaults + Compliance Bindings split ─────── */}
      <div className="grid grid-cols-[1fr_1.15fr] gap-3 mb-4">
        <Card>
          <Card.Header
            title={<><RefreshCw size={16} className="inline -mt-0.5 mr-1.5 text-v2-warning" />Rotation defaults</>}
            subtitle="Org-wide baseline. Tenants and agents can override."
          />
          <Card.Body>
            <div className="grid grid-cols-2 gap-2.5 text-[12px]">
              {ROTATION_DEFAULTS.map((r) => (
                <div key={r.label}>
                  <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold">{r.label}</div>
                  <div className="text-[13.5px] text-v2-ink font-semibold mt-0.5">{r.value}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 px-3 py-2 bg-v2-muted rounded-v2-sm text-[11px] text-v2-ink-secondary">
              2 tenants currently override these defaults
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header
            title={<><Scale size={16} className="inline -mt-0.5 mr-1.5 text-v2-action" />Compliance bindings</>}
            subtitle={
              <>
                Activates <span className="font-mono-v2 text-[11px]">industry_risk_default.rego</span> rules across the org
              </>
            }
          />
          <Card.Body>
            <div className="flex flex-col gap-1.5">
              {COMPLIANCE_BINDINGS.map((b) => (
                <div
                  key={b.id}
                  className={
                    'flex items-center justify-between px-3 py-1.5 border border-v2-border-subtle rounded-v2-md ' +
                    (b.disabled ? 'opacity-60' : '')
                  }
                >
                  <div className="flex items-center gap-2">
                    <div className={'w-1.5 h-1.5 rounded-full ' + (DOT_BG[b.dotTone] || 'bg-v2-border')} />
                    <div>
                      <div className="text-[13px] font-semibold text-v2-ink">
                        {b.framework}
                        {b.extra && <span className="text-[11px] text-v2-ink-muted font-normal ml-1">{b.extra}</span>}
                      </div>
                      <div className="text-[11px] text-v2-ink-secondary">
                        Tags <span className="font-mono-v2">{b.regoTag}</span>
                      </div>
                    </div>
                  </div>
                  <Pill tone={b.statusTone} size="sm">{b.status}</Pill>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* 6 — Recent Changes footer ─────────────────────────────── */}
      <Card padding="sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[13px] font-semibold text-v2-ink flex items-center gap-1.5">
            <History size={14} className="text-v2-ink-secondary" aria-hidden="true" />
            Recent configuration changes
          </div>
          <Button variant="ghost" size="xs" iconRight={<ArrowUpRight size={11} />}>View all</Button>
        </div>
        <div className="text-[12px]">
          {RECENT_CHANGES.map((c, i) => (
            <div
              key={i}
              className={
                'flex justify-between py-1.5 ' +
                (i < RECENT_CHANGES.length - 1 ? 'border-b border-v2-border-subtle' : '')
              }
            >
              <div>
                <span className="text-v2-ink">{c.action}</span>{' '}
                <span className="font-mono-v2 text-[11px] text-v2-ink-secondary">{c.target}</span>
                {c.note && <span className="text-v2-ink-muted"> {c.note}</span>}
              </div>
              <div className="text-v2-ink-muted text-[11px]">{c.who} · {c.when}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Stage 2c-2 — Edit slideout. Mounted INSIDE V2PageShell so the
          page chrome stays visible behind the panel. On save we full-
          reload the page so the displayed sections reflect the new
          server state (will switch to a refetch model once the page
          moves to useEffect-driven data fetching). */}
      <OrgConfigEditSlideout
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => { window.location.reload(); }}
      />
    </V2PageShell>
  );
}
